/**
 * As secoes da aba Configuracao: Automacao, Fotoperiodo, Estagio da Planta,
 * VPD, Camera e Seguranca.
 *
 * Responsabilidade: mostrar o que o digital twin diz que esta valendo, deixar
 * editar o que a nuvem tem comando para mudar, e publicar um comando por campo
 * alterado. Os titulos e a ordem espelham `gfc/firmware/data/index.html`
 * (`.sheet-card-title`) — quem aprendeu o painel local nao deveria reaprender
 * este.
 *
 * NAO e responsabilidade deste arquivo: validar faixa. `automation_cmd.cpp`
 * aceita qualquer float num setpoint e a PWA local tambem; inventar limite so
 * aqui faria o mesmo valor ser aceito por um transporte e recusado por outro
 * (ver `_valida_setpoint` em `api/app/schemas/command.py`). O que a API recusa
 * volta como 422 com o motivo, e o motivo aparece no aviso.
 *
 * ## Nem toda secao do gfc tem comando pela nuvem
 *
 * `docs/CONTRACT.md` §6 so define cinco categorias de MUTACAO: `setpoint`,
 * `photoperiod`, `override`, `switch`, `safety`. `plant_stage` e os offsets de
 * VPD (`leaf_offset_day/night`) so sao mutaveis pelo endpoint HTTP LOCAL do
 * firmware (`webserver.cpp`) — nunca passam por `automation_cmd_dispatch()` —,
 * e `cam_*` esta explicitamente listado como fora da superficie de comando da
 * nuvem. Por isso Estagio da Planta, VPD e Camera aqui sao cartoes SOMENTE
 * LEITURA: mostram o que o digital twin (§5) reporta, sem estepper nem botao.
 * Tratar isso como um detalhe de UI seria enganoso — falta comando no
 * contrato, nao falta campo na tela.
 *
 * ⚠️ A razao pela qual `cam_*` esta fora MUDOU na C11, e o texto do cartao
 * Camera junto. Ate ali a explicacao era "a nuvem nao escreve configuracao
 * enquanto o firmware nao verificar assinatura" — e a F9 fez o firmware
 * verificar, entao aquela frase virou mentira no dia em que `ota` entrou. O
 * motivo que resta e o do §6, e ele nao depende de fase nenhuma: `cam_*`
 * ESCREVE configuracao no aparelho, como o `dev_name` e o Wi-Fi ao lado deles.
 * `snapshot` dispara uma leitura e `ota` grava uma imagem que a nuvem nao
 * assinou — nenhum dos dois escreve configuracao.
 *
 * ## Um comando por campo, e nao um "salvar tudo"
 *
 * O contrato §6 nao tem comando composto: cada `setpoint`/`photoperiod`/
 * `safety` e uma linha com seu proprio ULID e seu proprio ack. Por isso cada
 * cartao editavel tem seu proprio botao "Aplicar alteracoes", escopado aos
 * campos daquele cartao — Automacao e Fotoperiodo nao tem por que compartilhar
 * um "sujo" so porque estao na mesma folha.
 *
 * ## Campo que nao sabe o proprio valor
 *
 * `stale_min` e o unico ajuste editavel aqui que o digital twin NAO reporta
 * (§5 tem `safety_alarm`, o resultado, nao o limite). Fica com o rotulo dizendo
 * isso, num cartao proprio ("Seguranca") — o gfc nao tem esta secao no painel
 * local porque la o alarme e so uma consequencia visivel, nao um ajuste.
 */

import * as acoes from "./../acoes";
import { nomeDoEstagio } from "./../metricas";
import * as push from "./../push";
import * as store from "./../store";
import type { CommandIn, PrefsNotificacao } from "./../tipos";
import { el, minutoDoDia, num, type Painel } from "./../ui";

interface Campo {
  rotulo: string;
  sufixo: string;
  input: HTMLInputElement;
  /** O que o aparelho diz que vale hoje. `null` = desconhecido. */
  doAparelho: () => string | null;
  comando: (valor: string) => CommandIn;
}

function numerico(
  rotulo: string,
  sufixo: string,
  passo: string,
  chave: "temp_on" | "temp_delta" | "rh_on" | "rh_delta",
  casas: number,
): Campo {
  const input = el("input", { type: "number", step: passo, inputmode: "decimal" });
  return {
    rotulo,
    sufixo,
    input,
    doAparelho: () => {
      const valor = store.aparelhoSelecionado()?.state?.setpoints?.[chave];
      return valor === undefined ? null : num(valor, casas);
    },
    comando: (valor) => ({ cmd: "setpoint", key: chave, value: valor }),
  };
}

function horario(rotulo: string, chave: "on" | "off"): Campo {
  const input = el("input", { type: "time" });
  return {
    rotulo,
    sufixo: "",
    input,
    doAparelho: () => {
      const setpoints = store.aparelhoSelecionado()?.state?.setpoints;
      const minuto = chave === "on" ? setpoints?.photo_on : setpoints?.photo_off;
      return minuto === undefined ? null : minutoDoDia(minuto);
    },
    comando: (valor) => ({ cmd: "photoperiod", key: chave, value: valor }),
  };
}

/** Cartao editavel: titulo, campos, e um "Aplicar alteracoes" escopado a eles. */
function cardEditavel(titulo: string, id: string, campos: Campo[]): Painel {
  const sujos = new Set<HTMLInputElement>();

  const salvar = el("button", {
    type: "button",
    class: "primary small",
    text: "Aplicar alterações",
    disabled: true,
  });

  for (const campo of campos) {
    campo.input.addEventListener("input", () => {
      sujos.add(campo.input);
      salvar.disabled = sujos.size === 0;
    });
  }

  salvar.addEventListener("click", () => {
    const alvo = store.aparelhoSelecionado();
    if (alvo === null) return;
    const pendentes = campos.filter(
      (c) => sujos.has(c.input) && c.input.value.trim() !== "",
    );
    sujos.clear();
    salvar.disabled = true;
    void (async () => {
      for (const campo of pendentes) {
        await acoes.comandar(
          alvo.device.id,
          campo.rotulo.toLowerCase(),
          campo.comando(campo.input.value.trim()),
        );
      }
    })();
  });

  const grade = el(
    "div",
    { class: "grade-ajustes" },
    ...campos.map((campo) =>
      el(
        "label",
        { class: "campo-linha" },
        el("span", { class: "campo-rotulo", text: campo.rotulo }),
        el(
          "span",
          { class: "campo-entrada" },
          campo.input,
          campo.sufixo !== "" && el("span", { class: "campo-sufixo", text: campo.sufixo }),
        ),
      ),
    ),
  );

  const raiz = el(
    "section",
    { class: "panel card", id },
    el(
      "div",
      { class: "panel-head" },
      el("span", { class: "panel-title", text: titulo }),
      salvar,
    ),
    grade,
  );

  function atualizar(): void {
    const temAparelho = store.aparelhoSelecionado() !== null;
    for (const campo of campos) {
      // Nao pisa no que o usuario esta digitando: um `state` retido chega a
      // cada 5 min (§5) e trocaria o valor por baixo do cursor.
      if (sujos.has(campo.input) || document.activeElement === campo.input) continue;
      const valor = campo.doAparelho();
      campo.input.value = valor ?? "";
      campo.input.disabled = !temAparelho;
    }
  }

  return { raiz, atualizar };
}

/** Cartao somente-leitura: titulo, uma linha de valor, e uma explicacao. */
function cardLeitura(titulo: string, id: string, explicacao: string): Painel & { valor: HTMLElement } {
  const valor = el("p", { class: "conta-email", text: "—" });
  const raiz = el(
    "section",
    { class: "panel card", id },
    el("div", { class: "panel-head" }, el("span", { class: "panel-title", text: titulo })),
    valor,
    el("p", { class: "hint", text: explicacao }),
  );
  return { raiz, valor, atualizar: () => {} };
}

function cardEstagio(): Painel {
  const card = cardLeitura(
    "Estágio da Planta",
    "ajustes-estagio",
    "Define a faixa ideal de VPD sombreada no gráfico de 24 h. Ajustado no " +
      "painel local do controlador (grow.local) — a nuvem ainda não tem um " +
      "comando para isso.",
  );
  function atualizar(): void {
    card.valor.textContent = nomeDoEstagio(store.aparelhoSelecionado()?.state?.plant_stage) ?? "—";
  }
  return { raiz: card.raiz, atualizar };
}

function cardVpd(): Painel {
  const card = cardLeitura(
    "VPD",
    "ajustes-vpd",
    "Faixa ideal calculada pelo aparelho a partir do estágio da planta. Os " +
      "offsets folha-ar (dia/noite) são ajustados no painel local do " +
      "controlador — a nuvem não os recebe nem os comanda.",
  );
  function atualizar(): void {
    const sensor = store.aparelhoSelecionado()?.state?.sensor;
    card.valor.textContent =
      sensor?.vpd_lo === undefined || sensor?.vpd_hi === undefined
        ? "—"
        : `${num(sensor.vpd_lo, 2)} – ${num(sensor.vpd_hi, 2)} kPa`;
  }
  return { raiz: card.raiz, atualizar };
}

function cardCamera(): Painel {
  const card = cardLeitura(
    "Câmera",
    "ajustes-camera",
    "Tirar uma foto agora é o botão “Capturar agora”, no painel da câmera. " +
      "Ligar o timelapse e girar a imagem continuam só no painel local do " +
      "controlador: são configuração do aparelho, e a nuvem não escreve " +
      "configuração no aparelho — só dispara leituras e atualizações.",
  );
  function atualizar(): void {
    const estado = store.aparelhoSelecionado()?.state;
    card.valor.textContent =
      estado?.cam_available === undefined
        ? "—"
        : estado.cam_available
          ? `disponível · timelapse ${estado.cam_timelapse ? "ativado" : "desativado"}`
          : "não disponível nesta placa";
  }
  return { raiz: card.raiz, atualizar };
}

/**
 * Notificacoes: o interruptor deste navegador, e o liga/desliga por tipo.
 *
 * Duas coisas diferentes num cartao so, e a diferenca importa:
 *
 * - **"Notificar neste aparelho"** e do NAVEGADOR. Ligar cria uma inscricao de
 *   Web Push que vale para este celular e mais nenhum, e desligar em um nao
 *   desliga nos outros.
 * - **Os tipos** sao da CONTA. Desligar "VPD" desliga para todo mundo que
 *   acessa esta estufa, e tambem tira o alerta do feed — nao so da bandeja.
 *   E paridade com o `tg_vpd_en` do firmware, que faz exatamente isso.
 *
 * ⚠️ **O bot do Telegram continua.** Nada desta tela o desliga, e e de
 * proposito: ele roda DENTRO do aparelho e funciona com a nuvem fora do ar
 * (§0). Estas notificacoes sao um segundo destino, nunca um substituto — e o
 * texto do cartao diz isso, porque um usuario que desligue o bot achando que a
 * nuvem cobre tudo fica sem alarme nenhum quando a WAN cair.
 */
function cardNotificacoes(): Painel {
  const estadoDoAparelho = el("p", { class: "conta-email", text: "—" });
  const botao = el("button", { type: "button", class: "primary small" }, "Ativar");
  const recado = el("p", { class: "hint", hidden: true });

  botao.addEventListener("click", () => {
    void (async () => {
      const ligado = store.obter().push.ligado;
      const erro = ligado ? await push.desligar() : await push.ligar();
      recado.hidden = erro === null;
      recado.textContent = erro ?? "";
    })();
  });

  const tipos: { chave: keyof PrefsNotificacao; rotulo: string; nota: string }[] = [
    {
      chave: "alarme_seguranca",
      rotulo: "Alarme de segurança",
      nota: "Sensor parou de atualizar. O mesmo alarme que o bot do Telegram envia.",
    },
    {
      chave: "vpd",
      rotulo: "VPD fora da faixa",
      nota: "Sai e volta da faixa ideal do estágio. Desligado por padrão, como no aparelho.",
    },
    {
      chave: "offline",
      rotulo: "Aparelho sem contato",
      nota: "Só a nuvem consegue avisar isto — o bot roda dentro do aparelho.",
    },
    {
      chave: "frames_perdidos",
      rotulo: "Frames de timelapse perdidos",
      nota: "Filmagem que não vai existir na nuvem. Só a nuvem sabe.",
    },
  ];

  const caixas = tipos.map((tipo) => {
    const caixa = el("input", { type: "checkbox" });
    caixa.addEventListener("change", () => {
      void acoes.alternarTipoDeAlerta(tipo.chave, caixa.checked);
    });
    const linha = el(
      "label",
      { class: "linha-pref" },
      caixa,
      el(
        "span",
        {},
        el("span", { class: "pref-rotulo", text: tipo.rotulo }),
        el("span", { class: "hint", text: tipo.nota }),
      ),
    );
    return { ...tipo, caixa, linha };
  });

  const raiz = el(
    "section",
    { class: "panel card", id: "ajustes-notificacoes" },
    el(
      "div",
      { class: "panel-head" },
      el("span", { class: "panel-title", text: "Notificações" }),
      botao,
    ),
    estadoDoAparelho,
    recado,
    ...caixas.map((c) => c.linha),
    el("p", {
      class: "hint",
      text:
        "O bot do Telegram do controlador continua funcionando e não é " +
        "desligado por nada aqui — ele avisa mesmo com a nuvem fora do ar.",
    }),
  );

  function atualizar(): void {
    const estado = store.obter();
    botao.disabled = !estado.push.suportado || estado.push.ocupado;
    botao.textContent = estado.push.ligado ? "Desativar" : "Ativar";
    estadoDoAparelho.textContent = !estado.push.suportado
      ? "Este navegador não recebe notificações. No iPhone, instale o app na tela de início."
      : estado.push.ligado
        ? "Ativado neste aparelho."
        : "Desativado neste aparelho.";

    for (const c of caixas) {
      // Sem preferencias carregadas os interruptores ficam travados em vez de
      // mostrar `false`: um "desligado" que na verdade e "ainda nao sei" faria
      // o usuario acreditar que nao recebe alarme de seguranca.
      c.caixa.disabled = estado.prefsNotificacao === null;
      c.caixa.checked = estado.prefsNotificacao?.[c.chave] ?? false;
    }
  }

  return { raiz, atualizar };
}

export function montar(): {
  automacao: Painel;
  fotoperiodo: Painel;
  estagio: Painel;
  vpd: Painel;
  camera: Painel;
  seguranca: Painel;
  notificacoes: Painel;
} {
  return {
    notificacoes: cardNotificacoes(),
    automacao: cardEditavel("Automação", "ajustes-automacao", [
      numerico("Liga o exaustor em", "°C", "0.1", "temp_on", 1),
      numerico("Histerese de temperatura", "°C", "0.1", "temp_delta", 1),
      numerico("Liga o umidificador em", "%", "0.1", "rh_on", 1),
      numerico("Histerese de umidade", "%", "0.1", "rh_delta", 1),
    ]),
    fotoperiodo: cardEditavel("Fotoperíodo", "ajustes-fotoperiodo", [
      horario("Luz acende", "on"),
      horario("Luz apaga", "off"),
    ]),
    estagio: cardEstagio(),
    vpd: cardVpd(),
    camera: cardCamera(),
    seguranca: cardEditavel("Segurança", "ajustes-seguranca", [
      {
        rotulo: "Alarme de leitura parada",
        sufixo: "min (não reportado pelo aparelho)",
        input: el("input", { type: "number", step: "1", min: "1", inputmode: "numeric" }),
        doAparelho: () => null,
        comando: (valor) => ({ cmd: "safety", key: "stale_min", value: valor }),
      },
    ]),
  };
}
